import React from 'react';
import {
  Form,
  Input,
  Radio,
  Select,
  FormInstance,
  InputNumber,
  Space,
} from 'antd';
import { useQuery } from '@tanstack/react-query';
import UploadImage from '@/components/UploadImage';
import { StyleFormData, StyleStatus, ModelNumber, ModelType } from '../types';
import {
  FORM_DEFAULT_VALUES,
  QUERY_KEYS,
  UPLOAD_IMAGE_SIZE,
} from '../constants';
import { getModelOptions, getStyleTypeOptions } from '../api';
import YesNoRadio from './YesNoRadio';

interface StyleFormProps {
  form: FormInstance<StyleFormData>;
}

const MODEL_TYPE_OPTIONS = [
  { label: 'flux', value: ModelType.FLUX },
  { label: 'sdxl', value: ModelType.SDXL },
] as const;

const StyleForm: React.FC<StyleFormProps> = ({ form }) => {
  const modelValue = Form.useWatch('model', form);
  const modelStr = String(modelValue ?? '');

  // SuppX（含SX/SX2.0）：全部高级功能 workFlowJson/pixel/细化/4K/Lora/局部重绘
  const isSuppX = modelStr === '5';
  // Flux Klein (Runware)：仅 Lora
  const isFluxKlein = modelStr === '20';
  // SuppFluxPro (Replicate flux-dev-lora)：Flux.1-dev 12B，原生支持 2 个 LoRA（URL）
  const isFluxPro = modelStr === '21';
  // SuppFluxPro 2.0 (Runware Flux.1-dev)：AIR ID 格式 LoRA
  const isFluxDevRunware = modelStr === '22';
  // SuppAnime Pro (Runware Illustrious-XL v0.1)：SDXL 动漫
  const isIllustrious = modelStr === '23';
  // SuppAnime Pro 2.0 (Runware NoobAI-XL Epsilon 1.1)：SDXL 动漫加强
  const isNoobAi = modelStr === '24';
  // Runware SDXL 系列（Illustrious / NoobAI 共用 LoRA 生态）
  const isRunwareSdxl = isIllustrious || isNoobAi;
  // Seedream 4.5（火山 Ark）：仅 1:1 比例支持 4K，其他高级功能不支持
  const isSeedream45 = modelStr === '19';
  // Seedream 5.0-lite（火山 Ark）：不支持 4K
  const isSeedream5 = modelStr === '18';
  // 支持 LoRA 的模型：SuppX / Flux Klein / SuppFluxPro / SuppFluxPro 2.0 / SuppAnime Pro / Pro 2.0
  const showLora = isSuppX || isFluxKlein || isFluxPro || isFluxDevRunware || isRunwareSdxl;

  // 获取模型编号列表
  const { data: modelOptions = [] } = useQuery({
    queryKey: [QUERY_KEYS.MODEL_OPTIONS],
    queryFn: getModelOptions,
  });

  // 获取风格类型列表
  const { data: styleTypeOptions = [] } = useQuery({
    queryKey: [QUERY_KEYS.STYLE_TYPE_OPTIONS],
    queryFn: getStyleTypeOptions,
  });

  return (
    <Form form={form} layout="vertical" initialValues={FORM_DEFAULT_VALUES}>
      {/* 基本信息 */}
      <Form.Item
        name="styleName"
        label="风格名称"
        rules={[{ required: true, message: '请输入风格名称' }]}
      >
        <Input placeholder="请输入风格名称" />
      </Form.Item>

      <Form.Item
        name="styleNameJa"
        label="风格名称日文"
        rules={[{ required: true, message: '请输入风格名称日文' }]}
      >
        <Input placeholder="请输入风格名称日文" />
      </Form.Item>

      <Form.Item
        name="styleImage"
        label="风格图片"
        rules={[{ required: true, message: '请上传风格图片' }]}
        extra="支持类型：webp"
      >
        <UploadImage
          width={UPLOAD_IMAGE_SIZE}
          height={UPLOAD_IMAGE_SIZE}
          showSizeHint={false}
          fileTypes={['image/webp']}
        />
      </Form.Item>

      <Form.Item
        name="description"
        label="描述(英)"
        rules={[{ required: true, message: '请输入描述(英)' }]}
      >
        <Input.TextArea placeholder="请输入描述(英)" rows={3} />
      </Form.Item>

      <Form.Item
        name="descriptionJa"
        label="描述(日)"
        rules={[{ required: true, message: '请输入描述(日)' }]}
      >
        <Input.TextArea placeholder="请输入描述(日)" rows={3} />
      </Form.Item>

      {/* 分类和模型 */}
      <Form.Item
        name="styleTypeId"
        label="风格分类id"
        rules={[{ required: true, message: '请选择风格分类id' }]}
      >
        <Select placeholder="请选择风格分类">
          {styleTypeOptions.map((option) => (
            <Select.Option key={option.styleTypeId} value={option.styleTypeId}>
              {option.styleType ||
                option.styleTypeJa ||
                `ID: ${option.styleTypeId}`}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        name="model"
        label="模型编号"
        rules={[{ required: true, message: '请选择模型编号' }]}
      >
        <Select placeholder="请选择模型编号">
          {modelOptions.map((option) => (
            <Select.Option key={option.model} value={option.model}>
              {option.name}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      {/* 仅 SuppX 显示子模型选择 */}
      {isSuppX && (
        <Form.Item
          name="modelType"
          label="子模型"
          rules={[{ required: true, message: '请选择子模型' }]}
        >
          <Select placeholder="请选择子模型">
            {MODEL_TYPE_OPTIONS.map((opt) => (
              <Select.Option key={opt.value} value={opt.value}>
                {opt.label}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      )}

      <Form.Item name="modelNameSuffix" label="模型名称后缀">
        <Input placeholder="请输入模型名称后缀" />
      </Form.Item>

      <Form.Item name="recommendSort" label="首页推荐排序">
        <InputNumber
          placeholder="请输入首页推荐排序"
          style={{ width: '100%' }}
        />
      </Form.Item>

      {/* 消耗数量 */}
      <Form.Item label="消耗数量:">
        <Space style={{ width: '100%' }}>
          <Form.Item
            label="充值金币"
            name="gcoins"
            style={{ flex: 1, margin: 0 }}
            rules={[{ required: true, message: '请输入充值金币数量' }]}
          >
            <InputNumber placeholder="充值金币" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            label="活动金币"
            name="pcoins"
            style={{ flex: 1, margin: 0 }}
            rules={[{ required: true, message: '请输入活动金币数量' }]}
          >
            <InputNumber placeholder="活动金币" style={{ width: '100%' }} />
          </Form.Item>
        </Space>
      </Form.Item>

      {/* 排序和状态 */}
      <Form.Item
        name="styleOrder"
        label="风格顺序"
        rules={[{ required: true, message: '请输入风格顺序' }]}
      >
        <InputNumber
          placeholder="请输入风格顺序"
          style={{ width: '100%' }}
        />
      </Form.Item>

      <Form.Item
        name="status"
        label="是否生效"
        rules={[{ required: true, message: '请选择是否生效' }]}
      >
        <Radio.Group>
          <Radio value={StyleStatus.DISABLED}>否</Radio>
          <Radio value={StyleStatus.ENABLED}>是</Radio>
        </Radio.Group>
      </Form.Item>

      {/* 功能配置 */}
      <Form.Item
        name="isSelfStyle"
        label="是否自定义风格"
        rules={[{ required: true, message: '请选择是否自定义风格' }]}
      >
        <YesNoRadio />
      </Form.Item>

      <Form.Item
        name="isVipStyle"
        label="是否会员风格"
        rules={[{ required: true, message: '请选择是否会员风格' }]}
      >
        <YesNoRadio />
      </Form.Item>

      <Form.Item
        name="minTierLevel"
        label="最低可用等级"
        tooltip="设置该风格所需的最低会员等级，前包含后（如 Lite → Lite/Pro/Premium 可用）"
      >
        <Select placeholder="请选择最低可用等级" allowClear>
          <Select.Option value={-1}>Free（所有用户可用）</Select.Option>
          <Select.Option value={2}>Lite（Lite/Pro/Premium 可用）</Select.Option>
          <Select.Option value={0}>Pro（Pro/Premium 可用）</Select.Option>
          <Select.Option value={1}>Premium（仅 Premium 可用）</Select.Option>
        </Select>
      </Form.Item>

      <Form.Item
        name="limitTime"
        label="是否期间限定"
        rules={[{ required: true, message: '请选择是否期间限定' }]}
      >
        <YesNoRadio />
      </Form.Item>

      <Form.Item
        name="defaultSelected"
        label="默认选中"
        rules={[{ required: true, message: '请选择是否默认选中' }]}
      >
        <YesNoRadio />
      </Form.Item>

      {/* 推荐配置 */}
      <Form.Item
        name="isRecommend"
        label="是否推荐到首页"
        rules={[{ required: true, message: '请选择是否推荐到首页' }]}
      >
        <YesNoRadio />
      </Form.Item>

      {/* ===== 仅 SuppX 需要的配置 ===== */}
      {isSuppX && (
        <>
          <div style={{ marginBottom: 16, padding: '8px 12px', background: '#f0f5ff', borderLeft: '3px solid #1677ff', fontSize: 13 }}>
            <strong>SuppX 专属配置</strong>（ComfyUI 工作流，支持细化/4K/局部重绘/LoRA）
          </div>
          <Form.Item
            name="pixel"
            label="像素"
            rules={[{ required: true, message: '请输入' }]}
          >
            <InputNumber min={0} precision={1} placeholder="像素" />
          </Form.Item>

          <Form.Item name="workFlowJson" label="工作流JSON">
            <Input.TextArea placeholder="请输入工作流JSON" rows={5} />
          </Form.Item>

          <Form.Item
            name="supportStep"
            label="支持细化(丰富细节)"
            initialValue={0}
          >
            <Radio.Group>
              <Radio value={0}>否</Radio>
              <Radio value={1}>是</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.supportStep !== cur.supportStep}>
            {({ getFieldValue }) =>
              getFieldValue('supportStep') === 1 ? (
                <Space style={{ width: '100%', marginBottom: 16 }}>
                  <Form.Item label="Step最小值" name="stepMin" initialValue={10} style={{ margin: 0 }}>
                    <InputNumber min={1} max={50} />
                  </Form.Item>
                  <Form.Item label="Step最大值" name="stepMax" initialValue={30} style={{ margin: 0 }}>
                    <InputNumber min={1} max={50} />
                  </Form.Item>
                  <Form.Item label="Step默认值" name="stepDefault" initialValue={15} style={{ margin: 0 }}>
                    <InputNumber min={1} max={50} />
                  </Form.Item>
                </Space>
              ) : null
            }
          </Form.Item>

          <Form.Item
            name="support4k"
            label="支持4K"
            initialValue={0}
          >
            <Radio.Group>
              <Radio value={0}>否</Radio>
              <Radio value={1}>是</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            name="supportInpaint"
            label="支持局部重绘"
            initialValue={0}
          >
            <Radio.Group>
              <Radio value={0}>否</Radio>
              <Radio value={1}>是</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.supportInpaint !== cur.supportInpaint}>
            {({ getFieldValue }) =>
              getFieldValue('supportInpaint') === 1 ? (
                <Form.Item
                  name="inpaintWorkflowId"
                  label="局部重绘工作流ID"
                  tooltip="SX/SX2.0自研风格需配置重绘工作流ID"
                >
                  <Input placeholder="请输入重绘工作流ID" />
                </Form.Item>
              ) : null
            }
          </Form.Item>
        </>
      )}

      {/* ===== LoRA 支持模型统一入口 ===== */}
      {showLora && (
        <>
          {isFluxKlein && (
            <div style={{ marginBottom: 16, padding: '8px 12px', background: '#f6ffed', borderLeft: '3px solid #52c41a', fontSize: 13 }}>
              <strong>SuppFlux 专属配置</strong>（Runware Flux Klein 9B，仅支持 Klein-native LoRA）
            </div>
          )}
          {isFluxPro && (
            <div style={{ marginBottom: 16, padding: '8px 12px', background: '#e6f4ff', borderLeft: '3px solid #1677ff', fontSize: 13 }}>
              <strong>SuppFluxPro 专属配置</strong>（Replicate flux-dev-lora，原生吃 Flux.1-dev 12B LoRA，最多 2 个）
            </div>
          )}
          {isFluxDevRunware && (
            <div style={{ marginBottom: 16, padding: '8px 12px', background: '#fff0f6', borderLeft: '3px solid #eb2f96', fontSize: 13 }}>
              <strong>SuppFluxPro 2.0 专属配置</strong>（Runware Flux.1-dev 12B，LoRA 用 AIR ID；需先上传到 Runware Dashboard）
            </div>
          )}
          {isIllustrious && (
            <div style={{ marginBottom: 16, padding: '8px 12px', background: '#fff7e6', borderLeft: '3px solid #fa8c16', fontSize: 13 }}>
              <strong>SuppAnime Pro 配置</strong>（Runware Illustrious-XL v0.1，SDXL 架构，LoRA 用 AIR ID；可用 CivitAI Illustrious LoRA）
            </div>
          )}
          {isNoobAi && (
            <div style={{ marginBottom: 16, padding: '8px 12px', background: '#f9f0ff', borderLeft: '3px solid #722ed1', fontSize: 13 }}>
              <strong>SuppAnime Pro 2.0 配置</strong>（Runware NoobAI-XL Epsilon 1.1，基于 Illustrious 继续训练，画质更强；可复用 Illustrious LoRA）
            </div>
          )}
          <Form.Item
            name="supportLora"
            label={(() => {
              if (isFluxKlein) return '启用 Lora (Runware Flux Klein)';
              if (isFluxPro) return '启用 Lora (Replicate Flux Dev)';
              if (isFluxDevRunware) return '启用 Lora (Runware Flux Dev)';
              if (isRunwareSdxl) return '启用 Lora (Runware SDXL)';
              return '支持Lora权重控制';
            })()}
            initialValue={0}
            tooltip={(() => {
              if (isFluxKlein) return 'Flux Klein 通过 Runware API 加载 LoRA，填 AIR ID (civitai:xxx@yyy 或 runware:xxx@yyy)';
              if (isFluxPro) return 'SuppFluxPro 通过 Replicate flux-dev-lora 加载 LoRA，填 HTTPS 直链 .safetensors（OSS/HuggingFace/CivitAI 均可），最多 2 个';
              if (isFluxDevRunware) return 'SuppFluxPro 2.0 通过 Runware 加载 LoRA，自训 LoRA 需先上传到 Runware Dashboard 拿 AIR ID (runware:xxx@yyy)，最多 2 个';
              if (isRunwareSdxl) return 'SuppAnime 系列通过 Runware 加载 SDXL LoRA，填 AIR ID (civitai:xxx@yyy)；Illustrious 和 NoobAI 的 LoRA 基本可通用';
              return 'SuppX 通过 ComfyUI 加载 LoRA，填本地文件路径';
            })()}
          >
            <Radio.Group>
              <Radio value={0}>否</Radio>
              <Radio value={1}>是</Radio>
            </Radio.Group>
          </Form.Item>
        </>
      )}

      {/* ===== 非 SuppX/Flux 模型提示（按模型区分能力） ===== */}
      {isSeedream45 && (
        <>
          <div style={{ marginBottom: 16, padding: '8px 12px', background: '#fffbe6', borderLeft: '3px solid #faad14', fontSize: 13 }}>
            <strong>提示：</strong>Seedream 4.5（火山 Ark API）支持 4K（仅 1:1 比例），不支持细化 / LoRA / 局部重绘
          </div>
          <Form.Item
            name="support4k"
            label="支持4K"
            initialValue={0}
            tooltip="Seedream 4.5 原生支持 4K，但仅在比例为 1:1 时生效，其他比例会回退到 2K"
          >
            <Radio.Group>
              <Radio value={0}>否</Radio>
              <Radio value={1}>是（仅 1:1）</Radio>
            </Radio.Group>
          </Form.Item>
        </>
      )}
      {isSeedream5 && (
        <div style={{ marginBottom: 16, padding: '8px 12px', background: '#fffbe6', borderLeft: '3px solid #faad14', fontSize: 13 }}>
          <strong>提示：</strong>Seedream 5.0-lite（火山 Ark API）不支持 4K / 细化 / LoRA / 局部重绘
        </div>
      )}
      {!isSuppX && !isFluxKlein && !isFluxPro && !isFluxDevRunware && !isRunwareSdxl && !isSeedream45 && !isSeedream5 && modelValue && (
        <div style={{ marginBottom: 16, padding: '8px 12px', background: '#fffbe6', borderLeft: '3px solid #faad14', fontSize: 13 }}>
          <strong>提示：</strong>当前模型（豆包等）通过外部 API 生图，不支持细化 / 4K / LoRA / 局部重绘等高级功能
        </div>
      )}

    </Form>
  );
};

export default StyleForm;
